import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/lib/supabase";
import { subDays, subMonths } from "date-fns";
import type { Subscription } from "@/hooks/useSubscription";
import { getPerConversationPrice, type Plan } from "@/hooks/usePlans";
import { useBillingOverview } from "@/hooks/useBillingOverview";
import type { BillingOverview } from "@/lib/billing-overview";
import { billingDayAsLocalDate } from "@/lib/billing-day";

export type PeriodStartSource =
  | "billing_overview"
  | "billing_period_bounds"
  | "organization_billing_cycle_start"
  | "last_billing_date"
  | "subscription_last_payment_at"
  | "subscription_activated_at"
  | "subscription_created_at"
  | "fallback_one_month";

export interface BillingSummary {
  periodStart: Date | null;
  /** Exclusive: the first instant that is no longer counted. Use it to query. */
  periodEnd: Date | null;
  /** The last day the period actually counts. Use it on screen. */
  periodEndInclusive: Date | null;
  periodStartSource: PeriodStartSource | null;
  /** The Explora plan counts every session ever, not the period's. */
  countsLifetime: boolean;
  baseCharge: number;
  conversationsUsed: number;
  conversationsIncluded: number;
  overageCount: number;
  overageCharge: number;
  totalEstimated: number;
  billingMode: "included" | "per_use";
  overview: BillingOverview | null;
  refreshOverview: () => void;
  loading: boolean;
}

export function useBillingSummary(
  orgId: string | null,
  subscription: Subscription | null,
  plan: Plan | null,
  currentCycleUsage?: number | null,
  billingCycleStart?: string | null,
  orgCreatedAt?: string | null,
): BillingSummary {
  const [sessionsCount, setSessionsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [usageWindow, setUsageWindow] = useState<{ start: Date; end: Date } | null>(null);
  const { overview, loading: overviewLoading, refresh: refreshOverview } = useBillingOverview(orgId);
  const overviewStart = overview?.activePeriod?.start ?? null;
  const overviewEnd = overview?.activePeriod?.end ?? null;
  const overviewUsage = overview?.usage.conversations ?? null;

  // The number on screen is `organizations.conversations_used`, which a database
  // trigger recomputes over `billing_period_bounds(anchor day)` — the day of the
  // month in billing_cycle_start, or of the organization's creation when it has
  // none. The period printed beside it used to come from the subscription's
  // payment dates instead, so the two described different windows: Patrimonio
  // read "1.637 conversaciones" under "4 sep → 20 sep", a period holding 434,
  // because its usage window is 26 ago → 26 sep. The label now asks the same
  // function the trigger uses.
  const anchorSource = billingCycleStart ?? orgCreatedAt ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!anchorSource) { setUsageWindow(null); return () => { cancelled = true; }; }
    // EXTRACT(DAY ...) in the trigger runs on the stored timestamptz with the
    // connection's UTC clock, so the anchor is read in UTC here too.
    const anchorDay = new Date(anchorSource).getUTCDate();
    if (!Number.isFinite(anchorDay)) { setUsageWindow(null); return () => { cancelled = true; }; }
    supabase
      .rpc("billing_period_bounds", { p_anchor_day: anchorDay, p_months: 1 })
      .then(({ data, error }) => {
        if (cancelled || error) return;
        const row = Array.isArray(data) ? data[0] : null;
        if (!row?.period_start || !row?.period_end) return;
        setUsageWindow({
          start: new Date(`${row.period_start}T00:00:00`),
          end: new Date(`${row.period_end}T00:00:00`),
        });
      });
    return () => { cancelled = true; };
  }, [anchorSource]);

  // Compute period:
  //   periodEnd   = next_billing_date
  //   periodStart = the most recent of last_payment_at, billing_cycle_start and
  //                 activated_at. A successful payment opens the cycle shown to
  //                 the customer, but a stale payment must not outrank the
  //                 cycle the org is actually in: taking the first match by
  //                 priority stretched the period by months for orgs whose last
  //                 recorded payment is old.
  const { periodStart, periodEnd, periodStartSource } = useMemo(() => {
    if (overviewStart && overviewEnd) {
      return {
        periodStart: new Date(`${overviewStart}T00:00:00`),
        periodEnd: new Date(`${overviewEnd}T00:00:00`),
        periodStartSource: "billing_overview" as const,
      };
    }
    if (usageWindow) {
      return { periodStart: usageWindow.start, periodEnd: usageWindow.end, periodStartSource: "billing_period_bounds" as const };
    }
    const periodEnd = billingDayAsLocalDate(subscription?.next_billing_date);
    if (!periodEnd) {
      return { periodStart: null, periodEnd: null, periodStartSource: null as PeriodStartSource | null };
    }

    const toValidDate = (value?: string | null) => {
      if (!value) return null;
      const dt = new Date(value);
      return !Number.isNaN(dt.getTime()) && dt < periodEnd ? dt : null;
    };

    const candidates: Array<{ date: Date; source: PeriodStartSource }> = [];
    const lastPaymentAt = toValidDate(subscription.last_payment_at);
    if (lastPaymentAt) candidates.push({ date: lastPaymentAt, source: "subscription_last_payment_at" });
    const explicitCycleStart = toValidDate(billingCycleStart);
    if (explicitCycleStart) candidates.push({ date: explicitCycleStart, source: "organization_billing_cycle_start" });
    const activatedAt = toValidDate(subscription.activated_at);
    if (activatedAt) candidates.push({ date: activatedAt, source: "subscription_activated_at" });

    const mostRecent = candidates.reduce<{ date: Date; source: PeriodStartSource } | null>(
      (best, candidate) => (!best || candidate.date > best.date ? candidate : best),
      null,
    );
    if (mostRecent) return { periodStart: mostRecent.date, periodEnd, periodStartSource: mostRecent.source };

    return { periodStart: subMonths(periodEnd, 1), periodEnd, periodStartSource: "fallback_one_month" as const };
  }, [overviewStart, overviewEnd, usageWindow, subscription?.next_billing_date, billingCycleStart, (subscription as any)?.last_billing_date, subscription?.last_payment_at, subscription?.activated_at, subscription?.created_at]);

  useEffect(() => {
    let cancelled = false;
    if (overviewLoading) {
      setLoading(true);
      return () => { cancelled = true; };
    }
    if (overviewUsage !== null) {
      setSessionsCount(Math.max(0, overviewUsage));
      setLoading(false);
      return () => { cancelled = true; };
    }
    const normalizedCurrentUsage = currentCycleUsage == null ? null : Number(currentCycleUsage);

    if (normalizedCurrentUsage !== null && Number.isFinite(normalizedCurrentUsage)) {
      setSessionsCount(Math.max(0, normalizedCurrentUsage));
      setLoading(false);
      return () => { cancelled = true; };
    }

    if (!orgId || !periodStart || !periodEnd) {
      setLoading(false);
      return () => { cancelled = true; };
    }

    setLoading(true);
    const startISO = periodStart.toISOString();
    const endISO = periodEnd.toISOString();

    supabase
      .rpc("org_sessions_in_range", { p_org: orgId, p_from: startISO, p_to: endISO })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error) setSessionsCount(Number(data ?? 0));
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [orgId, periodStart, periodEnd, currentCycleUsage, overviewLoading, overviewUsage]);

  return useMemo(() => {
    if (!plan || !periodStart || !periodEnd) {
      return {
        periodStart: null,
        periodEnd: null,
        periodEndInclusive: null,
        periodStartSource: null,
        countsLifetime: plan?.id === "explora",
        baseCharge: 0,
        conversationsUsed: 0,
        conversationsIncluded: 0,
        overageCount: 0,
        overageCharge: 0,
        totalEstimated: 0,
        billingMode: "included" as const,
        overview,
        refreshOverview,
        loading,
      };
    }

    const mode = (plan.billing_mode === "per_use" ? "per_use" : "included") as "included" | "per_use";
    const included = plan.conversations_included ?? 0;
    const perConvo = getPerConversationPrice(plan);
    const base = mode === "included" ? (plan.price_monthly ?? 0) : 0;
    const overage = mode === "included" ? Math.max(0, sessionsCount - included) : sessionsCount;
    const overageCharge = overage * perConvo;

    return {
      periodStart,
      periodEnd,
      periodEndInclusive: subDays(periodEnd, 1),
      periodStartSource,
      countsLifetime: plan.id === "explora",
      baseCharge: base,
      conversationsUsed: sessionsCount,
      conversationsIncluded: included,
      overageCount: overage,
      overageCharge,
      totalEstimated: base + overageCharge,
      billingMode: mode,
      overview,
      refreshOverview,
      loading,
    };
  }, [plan, periodStart, periodEnd, periodStartSource, sessionsCount, loading, overview, refreshOverview]);
}
